import { useEffect, useState } from 'react';
import { AlertTriangle, Check, PackageX } from 'lucide-react';
import type { ImpactoRetiroVista } from '@shared/types';
import { formatPrecio } from '@renderer/utils/formatters';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';

/**
 * Sprint 60 — "Dejar de vender", con la decisión de cada paquete a la vista.
 *
 * Sustituye al botón "Eliminar", que sólo sabía fallar con un mensaje sobre
 * integridad referencial. Y va más allá de avisar: el reporte de campo del
 * 2026-08-05 mostró que **avisar no alcanza**. La dueña retiró un refresco, dos
 * paquetes quedaron invendibles y lo descubrió días después en otra pantalla.
 *
 * Así que aquí no se pregunta "¿estás seguro?". Se muestra qué paquetes dependen
 * del producto y se resuelve cada uno **en el momento**, que es cuando el dueño
 * tiene el contexto en la cabeza. Si se deja para después, nadie vuelve.
 *
 * Tres decisiones por paquete, y la de quitar el renglón es la más probable cuando
 * el negocio simplemente dejó de ofrecer algo:
 *   · Sustituir por otro producto (sólo se ofrecen los que se pueden servir hoy).
 *   · Quitar del paquete.
 *   · Dejarlo opcional — sigue en la receta pero no bloquea la venta.
 *
 * Y el precio se decide aparte, con el importe exacto a la vista, porque mover el
 * precio de un paquete es una decisión comercial y no un efecto secundario.
 */

type Accion = 'sustituir' | 'quitar' | 'opcional';

interface DecisionUI {
  accion: Accion;
  sustitutoId?: number;
}

interface Props {
  productoId: number;
  productoNombre: string;
  onCerrar: () => void;
  onListo: () => void;
}

export function RetirarProductoModal({ productoId, productoNombre, onCerrar, onListo }: Props) {
  const [impacto, setImpacto] = useState<ImpactoRetiroVista | null>(null);
  const [decisiones, setDecisiones] = useState<Record<number, DecisionUI>>({});
  const [ajustar, setAjustar] = useState<Record<number, boolean>>({});
  const [cargando, setCargando] = useState(true);
  const [aplicando, setAplicando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resumen, setResumen] = useState<string[] | null>(null);

  useEffect(() => {
    (async () => {
      const res = await window.api.productos.impactoRetiro(productoId);
      if (res.ok) {
        setImpacto(res.data);
        // Arranca con la opción más probable cuando el negocio deja de ofrecer
        // algo: que el paquete ya no lo lleve. El dueño la cambia si quiere.
        const iniciales: Record<number, DecisionUI> = {};
        for (const p of res.data.paquetesAfectados) {
          for (const r of p.renglones) iniciales[r.renglonId] = { accion: 'quitar' };
        }
        setDecisiones(iniciales);
      } else {
        setError(traducirErrorBackend(res.error, res.codigo));
      }
      setCargando(false);
    })();
  }, [productoId]);

  /** Diferencia de precio que implicaría lo decidido, por paquete. */
  const deltaDe = (paqueteId: number): number => {
    if (!impacto) return 0;
    const paquete = impacto.paquetesAfectados.find((p) => p.paqueteId === paqueteId);
    if (!paquete) return 0;
    let delta = 0;
    for (const r of paquete.renglones) {
      const d = decisiones[r.renglonId];
      if (!d) continue;
      if (d.accion === 'quitar') delta -= r.valorEnPaquete;
      if (d.accion === 'sustituir' && d.sustitutoId) {
        const alt = r.alternativas.find((a) => a.productoId === d.sustitutoId);
        if (alt) delta += alt.valorEnPaquete - r.valorEnPaquete;
      }
      // 'opcional' no cambia el precio: el producto sigue incluido si lo piden.
    }
    return delta;
  };

  const aplicar = async () => {
    if (!impacto) return;
    setAplicando(true);
    setError(null);

    const res = await window.api.productos.retirar({
      productoId,
      decisiones: Object.entries(decisiones).map(([renglonId, d]) => ({
        renglonId: Number(renglonId),
        accion: d.accion,
        sustitutoId: d.accion === 'sustituir' ? d.sustitutoId : undefined,
      })),
      ajustarPrecioDe: Object.entries(ajustar)
        .filter(([, v]) => v)
        .map(([id]) => Number(id)),
    });

    setAplicando(false);
    if (res.ok) setResumen(res.data.resumen);
    else setError(traducirErrorBackend(res.error, res.codigo));
  };

  const faltaElegirSustituto = Object.values(decisiones).some(
    (d) => d.accion === 'sustituir' && !d.sustitutoId,
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[92vh] w-full max-w-2xl flex-col rounded-xl bg-white shadow-xl">
        <header className="flex items-center gap-2 border-b border-slate-200 px-5 py-3">
          <PackageX className="h-5 w-5 text-amber-600" />
          <h2 className="font-semibold">Dejar de vender: {productoNombre}</h2>
        </header>

        <div className="flex-1 space-y-4 overflow-auto p-5">
          {cargando && <p className="py-6 text-center text-slate-400">Revisando dónde se usa…</p>}

          {/* ── Ya está hecho: se dice qué cambió y se cierra ── */}
          {resumen && (
            <div className="rounded-lg border-2 border-green-300 bg-green-50 p-4">
              <p className="flex items-center gap-2 font-semibold text-green-900">
                <Check className="h-5 w-5" /> Listo
              </p>
              <ul className="mt-2 space-y-1 text-sm text-green-900">
                {resumen.map((l, i) => (
                  <li key={i}>• {l}</li>
                ))}
              </ul>
            </div>
          )}

          {!cargando && !resumen && impacto && (
            <>
              {impacto.ventasHistoricas > 0 && (
                <p className="rounded-lg bg-slate-100 px-4 py-3 text-sm text-slate-700">
                  Este producto tiene <strong>{impacto.ventasHistoricas} venta(s)</strong> en el
                  historial, así que no se borra: se retira del menú y las ventas viejas quedan
                  intactas. Puedes volver a venderlo cuando quieras.
                </p>
              )}

              {impacto.paquetesAfectados.length === 0 ? (
                <p className="rounded-lg bg-green-50 px-4 py-3 text-sm text-green-900">
                  No forma parte de ningún paquete. Se puede retirar sin más.
                </p>
              ) : (
                <>
                  <div className="rounded-lg border-2 border-amber-300 bg-amber-50 px-4 py-3">
                    <p className="flex items-center gap-2 font-semibold text-amber-900">
                      <AlertTriangle className="h-5 w-5" />
                      Está en {impacto.paquetesAfectados.length} paquete(s)
                    </p>
                    <p className="mt-1 text-sm text-amber-900">
                      Decide qué hacer en cada uno. Si no lo decides, esos paquetes se quedarían
                      sin poder venderse — y eso no se nota hasta que alguien intenta cobrarlos.
                    </p>
                  </div>

                  {impacto.paquetesAfectados.map((p) => {
                    const delta = deltaDe(p.paqueteId);
                    return (
                      <div key={p.paqueteId} className="rounded-lg border border-slate-200 p-4">
                        <div className="flex items-baseline justify-between gap-2">
                          <h3 className="font-semibold">{p.nombre}</h3>
                          <span className="text-sm text-slate-500">
                            {formatPrecio(p.precioBase, '$', 'es-MX')}
                            {p.vendiblesHoy !== null && ` · ${p.vendiblesHoy} vendibles hoy`}
                          </span>
                        </div>

                        {p.renglones.map((r) => (
                          <div key={r.renglonId} className="mt-3 border-t border-slate-100 pt-3">
                            <p className="text-sm text-slate-600">
                              Lleva <strong>{r.cantidad}</strong> · vale{' '}
                              {formatPrecio(r.valorEnPaquete, '$', 'es-MX')}
                              {!r.esObligatorio && ' · ya es opcional'}
                            </p>

                            <div className="mt-2 space-y-2">
                              <Opcion
                                nombre={`accion-${r.renglonId}`}
                                marcada={decisiones[r.renglonId]?.accion === 'quitar'}
                                onElegir={() =>
                                  setDecisiones((d) => ({ ...d, [r.renglonId]: { accion: 'quitar' } }))
                                }
                                etiqueta="Quitar del paquete"
                                ayuda="El paquete deja de incluirlo."
                              />

                              <Opcion
                                nombre={`accion-${r.renglonId}`}
                                marcada={decisiones[r.renglonId]?.accion === 'sustituir'}
                                onElegir={() =>
                                  setDecisiones((d) => ({
                                    ...d,
                                    [r.renglonId]: {
                                      accion: 'sustituir',
                                      sustitutoId: d[r.renglonId]?.sustitutoId,
                                    },
                                  }))
                                }
                                etiqueta="Cambiarlo por otro producto"
                                ayuda="Sólo se ofrecen los que se pueden servir hoy."
                              >
                                {decisiones[r.renglonId]?.accion === 'sustituir' && (
                                  <select
                                    className="input mt-2"
                                    value={decisiones[r.renglonId]?.sustitutoId ?? ''}
                                    onChange={(e) =>
                                      setDecisiones((d) => ({
                                        ...d,
                                        [r.renglonId]: {
                                          accion: 'sustituir',
                                          sustitutoId: Number(e.target.value) || undefined,
                                        },
                                      }))
                                    }
                                  >
                                    <option value="">— Elige el reemplazo —</option>
                                    {r.alternativas.map((a) => (
                                      <option key={a.productoId} value={a.productoId}>
                                        {a.nombre}
                                        {a.disponibles === null
                                          ? ''
                                          : a.disponibles > 0
                                            ? ` — ${a.disponibles} disponibles`
                                            : ' — sin existencias'}
                                        {` · ${formatPrecio(a.valorEnPaquete, '$', 'es-MX')}`}
                                      </option>
                                    ))}
                                  </select>
                                )}
                              </Opcion>

                              <Opcion
                                nombre={`accion-${r.renglonId}`}
                                marcada={decisiones[r.renglonId]?.accion === 'opcional'}
                                onElegir={() =>
                                  setDecisiones((d) => ({ ...d, [r.renglonId]: { accion: 'opcional' } }))
                                }
                                etiqueta="Dejarlo opcional"
                                ayuda="Sigue en la receta, pero no impide vender el paquete."
                              />
                            </div>
                          </div>
                        ))}

                        {/* El precio, sólo si lo decidido lo movería */}
                        {delta !== 0 && (
                          <div className="mt-3 rounded-md bg-slate-50 p-3">
                            <p className="text-sm font-medium text-slate-700">Precio del paquete</p>
                            <label className="mt-1 flex items-start gap-2 text-sm">
                              <input
                                type="radio"
                                className="mt-1"
                                checked={!ajustar[p.paqueteId]}
                                onChange={() => setAjustar((a) => ({ ...a, [p.paqueteId]: false }))}
                              />
                              <span>
                                Mantener {formatPrecio(p.precioBase, '$', 'es-MX')}
                                <span className="block text-xs text-slate-500">
                                  El negocio absorbe la diferencia.
                                </span>
                              </span>
                            </label>
                            <label className="mt-1 flex items-start gap-2 text-sm">
                              <input
                                type="radio"
                                className="mt-1"
                                checked={!!ajustar[p.paqueteId]}
                                onChange={() => setAjustar((a) => ({ ...a, [p.paqueteId]: true }))}
                              />
                              <span>
                                Ajustar a{' '}
                                <strong>
                                  {formatPrecio(Math.max(0, p.precioBase + delta), '$', 'es-MX')}
                                </strong>{' '}
                                <span className="text-slate-500">
                                  ({delta > 0 ? '+' : '−'}
                                  {formatPrecio(Math.abs(delta), '$', 'es-MX')})
                                </span>
                              </span>
                            </label>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </>
              )}

              {impacto.comoAlternativa.length > 0 && (
                <p className="text-sm text-slate-600">
                  También dejará de ofrecerse como alternativa en{' '}
                  {impacto.comoAlternativa.length} renglón(es) de paquete. Eso se limpia solo.
                </p>
              )}
            </>
          )}

          {error && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-800">{error}</p>}
        </div>

        <footer className="flex gap-2 border-t border-slate-200 px-5 py-3">
          {resumen ? (
            <button onClick={onListo} className="btn-primary ml-auto min-h-[44px]">
              Entendido
            </button>
          ) : (
            <>
              <button onClick={onCerrar} className="btn-ghost flex-1">
                Cancelar
              </button>
              <button
                onClick={aplicar}
                disabled={cargando || aplicando || faltaElegirSustituto}
                className="btn-primary flex-1 min-h-[44px]"
              >
                {aplicando ? 'Aplicando…' : 'Dejar de vender'}
              </button>
            </>
          )}
        </footer>
      </div>
    </div>
  );
}

/** Una opción de radio con su ayuda, para no repetir el markup tres veces. */
function Opcion({
  nombre,
  marcada,
  onElegir,
  etiqueta,
  ayuda,
  children,
}: {
  nombre: string;
  marcada: boolean;
  onElegir: () => void;
  etiqueta: string;
  ayuda: string;
  children?: React.ReactNode;
}) {
  return (
    <label className="block cursor-pointer rounded-md px-2 py-1.5 hover:bg-slate-50">
      <span className="flex items-start gap-2">
        <input type="radio" name={nombre} checked={marcada} onChange={onElegir} className="mt-1" />
        <span className="text-sm">
          <span className="font-medium">{etiqueta}</span>
          <span className="block text-xs text-slate-500">{ayuda}</span>
        </span>
      </span>
      {children}
    </label>
  );
}
